import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { usePrefersReducedMotion } from '@mc/hooks'
import classes from './RotatingTitle.module.scss'

export const HOLD_MS = 4000
export const SLIDE_MS = 300
const PHRASE_COUNT = 2

type RotatingTitleProps = {
  lead: React.ReactNode
  from: React.ReactNode
  to: React.ReactNode
  keepWidth?: boolean
}

export const RotatingTitle = ({
  lead,
  from,
  to,
  keepWidth = true,
}: RotatingTitleProps) => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const [index, setIndex] = useState(0)
  const [isSnapping, setIsSnapping] = useState(false)
  const [widths, setWidths] = useState<number[]>([])
  const lineRefs = useRef<Array<HTMLSpanElement | null>>([])

  // The trailing copy of the first phrase keeps every step sliding up before the snap back.
  const lines = [from, to, from]

  useEffect(() => {
    const id = setInterval(() => {
      setIsSnapping(false)
      setIndex((current) =>
        prefersReducedMotion ? (current + 1) % PHRASE_COUNT : current + 1,
      )
    }, HOLD_MS + SLIDE_MS)

    return () => clearInterval(id)
  }, [prefersReducedMotion])

  useEffect(() => {
    if (index < PHRASE_COUNT) return undefined

    const id = setTimeout(() => {
      setIsSnapping(true)
      setIndex(0)
    }, SLIDE_MS)

    return () => clearTimeout(id)
  }, [index])

  useEffect(() => {
    const measure = () =>
      setWidths(lineRefs.current.map((line) => line?.offsetWidth ?? 0))

    measure()
    if (typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver(measure)
    lineRefs.current.forEach((line) => line && observer.observe(line))

    return () => observer.disconnect()
  }, [])

  const slotWidth = widths[index] || undefined
  const spacerWidth = slotWidth && Math.max(...widths) - slotWidth

  return (
    <>
      {keepWidth && (
        <span
          className={`d-none d-md-inline-block ${classes.spacer}`}
          style={{ width: spacerWidth }}
          aria-hidden='true'
        />
      )}
      {lead}
      <span
        className={classNames(classes.slot, { [classes.snapping]: isSnapping })}
        style={{ width: slotWidth }}
      >
        {lines.map((line, lineIndex) => (
          <span
            key={lineIndex}
            ref={(element) => {
              lineRefs.current[lineIndex] = element
            }}
            className={classes.line}
            style={{ transform: `translateY(${(lineIndex - index) * 100}%)` }}
            aria-hidden={lineIndex !== index}
          >
            {line}
          </span>
        ))}
      </span>
    </>
  )
}
